import { useEffect, useState } from 'react';
import {
    CpmButton,
    CpmModal,
    CpmModalBody,
    CpmModalFooter,
} from '../../../../components/design-system';
import { bulkUpsertMasters } from '../../../../services/productMasterService';
import type { CpmBulkUpsertItem } from '../../../../types/centralProductMaster.types';
import { getApiErrorMessage } from '../../../../utils/api/ApiClient';
import { toast } from '../../../../utils/toast';

interface DiffRow {
    name: string;
    kindLabel: string;
    field: string;
    from: string;
    to: string;
}

interface BulkConfirmDialogProps {
    open: boolean;
    items: CpmBulkUpsertItem[];
    nameByKey: Record<string, string>;
    onClose: () => void;
    onCommitted: () => void;
}

function displayVal(v: unknown): string {
    if (v == null || v === '') return '—';
    if (Array.isArray(v)) return v.length ? v.map(String).join(', ') : '—';
    return String(v);
}

function fieldLabel(field: string): string {
    const map: Record<string, string> = {
        name: 'Name',
        aliases: 'Aliases',
        countryOfOrigin: 'Country of origin',
        parentCompany: 'Parent company',
        alcoholContent: 'Alcohol content',
        description: 'Description',
        containerType: 'Container',
        itemPerCase: 'Per case',
        gtin: 'GTIN',
        retailPrice: 'Retail price',
        purchasePrice: 'Purchase price',
        wholesalePrice: 'Wholesale price',
        distilleryPrice: 'Distillery price',
        exciseCategory: 'Excise category',
        exciseSubCategory: 'Subcategory',
        licenseType: 'License type',
        itemCode: 'Item codes',
    };
    return map[field] ?? field;
}

function kindLabel(level: string): string {
    if (level === 'brand') return 'Brand';
    if (level === 'product') return 'Product';
    if (level === 'variant') return 'Variant';
    return 'State';
}

export default function BulkConfirmDialog({
    open,
    items,
    nameByKey,
    onClose,
    onCommitted,
}: BulkConfirmDialogProps) {
    const [submitting, setSubmitting] = useState(false);
    const [commitProgress, setCommitProgress] = useState<string | null>(null);
    const [commitError, setCommitError] = useState<string | null>(null);
    const [previewRows, setPreviewRows] = useState<DiffRow[] | null>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const loadPreview = async () => {
        try {
            setLoadingPreview(true);
            setPreviewError(null);
            const data = await bulkUpsertMasters({ dryRun: true, items });
            const rows: DiffRow[] = [];
            for (const result of data.results) {
                const item = items[result.index];
                if (!item || !result.ok) continue;
                const key = `${item.level}:${item.id}`;
                const name = nameByKey[key] ?? item.id;
                for (const field of Object.keys(item.fields)) {
                    rows.push({
                        name,
                        kindLabel: kindLabel(item.level),
                        field: fieldLabel(field),
                        from: displayVal(result.before?.[field]),
                        to: displayVal(result.after?.[field] ?? item.fields[field]),
                    });
                }
            }
            setPreviewRows(rows);
        } catch (err) {
            setPreviewError(getApiErrorMessage(err));
        } finally {
            setLoadingPreview(false);
        }
    };

    useEffect(() => {
        if (open && items.length > 0) {
            void loadPreview();
        } else if (!open) {
            setPreviewRows(null);
            setPreviewError(null);
            setCommitError(null);
            setCommitProgress(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, items]);

    const commit = async () => {
        try {
            setSubmitting(true);
            setCommitError(null);
            setCommitProgress(
                items.length === 1
                    ? 'Writing record 1 of 1'
                    : `Writing record 1 of ${items.length}`
            );
            const data = await bulkUpsertMasters({ dryRun: false, items });
            const failed = (data.results ?? []).filter((r) => !r.ok).length;
            const total = items.length;
            if (failed === total) {
                setCommitError('Nothing was committed');
                setCommitProgress(null);
                return;
            }
            if (failed > 0) {
                toast.error(`${failed} of ${total} changes failed — the rest were saved`);
            } else {
                toast.success(`Saved ${total} ${total === 1 ? 'change' : 'changes'}`);
            }
            onCommitted();
            onClose();
        } catch (err) {
            setCommitError(getApiErrorMessage(err) || 'Nothing was committed');
            setCommitProgress(null);
        } finally {
            setSubmitting(false);
        }
    };

    const mixedLevels = new Set(items.map((i) => i.level)).size > 1;

    return (
        <CpmModal open={open} onClose={onClose} maxWidth={760} zIndex={74}>
            <div
                className="flex items-start gap-[14px] border-b border-[var(--cpm-border-default)]"
                style={{ padding: '22px 24px 16px' }}
            >
                <span className="shrink-0 inline-flex items-center justify-center w-[38px] h-[38px] rounded-[10px] bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]">
                    <svg
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                    >
                        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
                <div className="flex-1 min-w-0 flex flex-col" style={{ gap: 6 }}>
                    <h2 className="m-0 !text-[20px] !font-extrabold !tracking-[-0.02em] !leading-[1.2] !text-[var(--cpm-brand-navy)]">
                        Confirm {items.length} {items.length === 1 ? 'change' : 'changes'}
                    </h2>
                    <p className="m-0 !text-[13px] !font-semibold !leading-[1.5] !text-[var(--cpm-text-secondary)]">
                        Review the before / after values, then commit. Nothing has been written yet.
                    </p>
                </div>
            </div>

            <div
                className="cpm-table-head grid bg-[var(--cpm-surface-subtle)] border-b border-[var(--cpm-border-default)]"
                style={{
                    gridTemplateColumns: 'minmax(0,1.25fr) 150px minmax(0,1fr) minmax(0,1fr)',
                    gap: 12,
                    padding: '10px 24px',
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    color: 'var(--cpm-text-primary)',
                    fontFamily: "var(--cpm-font-sans), 'Inter', system-ui, sans-serif",
                }}
            >
                <span>{mixedLevels ? 'Record' : 'Name'}</span>
                <span>Field</span>
                <span>Current</span>
                <span>New value</span>
            </div>

            <CpmModalBody className="!p-0 max-h-[50vh] overflow-y-auto">
                {loadingPreview ? (
                    <div className="px-6 py-10 text-center text-[13px] font-semibold text-[var(--cpm-text-secondary)]">
                        Previewing changes…
                    </div>
                ) : previewError ? (
                    <div className="px-6 py-10 text-center text-[13px] font-semibold text-[var(--cpm-shortfall-text)]">
                        {previewError}
                    </div>
                ) : (previewRows ?? []).length === 0 ? (
                    <div className="px-6 py-10 text-center text-[13px] font-semibold text-[var(--cpm-text-secondary)]">
                        No field changes to commit.
                    </div>
                ) : (
                    (previewRows ?? []).map((row, i) => (
                        <div
                            key={`${row.name}-${row.field}-${i}`}
                            className="grid items-center border-b border-[var(--cpm-border-default)]"
                            style={{
                                gridTemplateColumns:
                                    'minmax(0,1.25fr) 150px minmax(0,1fr) minmax(0,1fr)',
                                gap: 12,
                                padding: '12px 24px',
                            }}
                        >
                            <span className="flex items-center gap-2 min-w-0 text-[12.5px] font-extrabold text-[var(--cpm-text-primary)]">
                                <span className="truncate">{row.name}</span>
                                {mixedLevels ? (
                                    <span className="shrink-0 px-[7px] py-[2px] rounded-full bg-[var(--cpm-surface-skeleton)] text-[9.5px] font-extrabold tracking-[0.05em] uppercase text-[var(--cpm-text-secondary)]">
                                        {row.kindLabel}
                                    </span>
                                ) : null}
                            </span>
                            <span className="text-[11.5px] font-bold text-[var(--cpm-text-secondary)]">
                                {row.field}
                            </span>
                            <span className="text-[12.5px] font-bold text-[var(--cpm-text-tertiary)] truncate">
                                {row.from}
                            </span>
                            <span className="text-[12.5px] font-extrabold text-[var(--cpm-brand-purple)] truncate">
                                {row.to}
                            </span>
                        </div>
                    ))
                )}
            </CpmModalBody>

            <CpmModalFooter className="justify-between gap-3 flex-wrap">
                <span
                    className="text-[11.5px] font-bold"
                    style={{
                        color: commitError
                            ? 'var(--cpm-shortfall-text)'
                            : 'var(--cpm-text-secondary)',
                    }}
                >
                    {commitError
                        ? commitError
                        : commitProgress
                          ? commitProgress
                          : 'Nothing has been written yet.'}
                </span>
                <div className="ml-auto flex items-center gap-[10px]">
                    <CpmButton
                        variant="outline"
                        size="modal"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Back to editing
                    </CpmButton>
                    <CpmButton
                        variant="navy"
                        size="modal"
                        onClick={commit}
                        isLoading={submitting}
                        disabled={loadingPreview || !!previewError || !previewRows?.length}
                    >
                        {submitting
                            ? 'Writing…'
                            : commitError
                              ? 'Try again'
                              : `Commit ${items.length} ${items.length === 1 ? 'change' : 'changes'}`}
                    </CpmButton>
                </div>
            </CpmModalFooter>
        </CpmModal>
    );
}
